/**
 * 🌍 GLOBAL STANDART KOD (TÜM PROJELER İÇİN GEÇERLİDİR)
 *
 * Bu dosya QubePro ekosisteminde ortak (global) standarttır.
 * Tek kaynak `qubepro-core` reposudur; uydu repolarda kalıcı değişiklik yapılmaz.
 * Değişiklik gerekiyorsa Core'da güncellenir ve senkron ile diğer repolara yayılır.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { FiUser } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import {
  SYSTEM_COMPANY_SCOPE_ID,
  canUseSystemCompanyScope,
  getSystemCompanyScopeLabel,
  useOptionalCompany,
} from '../../context/CompanyContext';
import { useTranslation } from '../../hooks/useTranslation';
import { useRoleAccess } from '../../hooks/useRoleAccess';
import { Icon } from './ComponentIcon';
import { AppearancePreferencesView } from './AppearancePreferencesView';
import { ProfileLanguageSelect } from './ProfileMenuSurface';
import { useTheme } from '../../context/ThemeContext';
import { useModalControlPlacement } from '../../hooks/useModalControlPlacement';
import type { ModalControlPlacementPreference } from '../../utils/modal-control-placement.utils';
import type { Theme } from '../../context/theme-context';
import {
  getDmsFileImageStableUrl,
  prefetchDmsImages,
  resolveCompanyLogoMediaScopeId,
  resolveSystemMediaCallerScopeId,
} from '../../dms';
import axios from '../../api/axios';
import { NATIVE_LANGUAGE_NAMES, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../utils/language.utils';
import {
  resolveCoreAssetUrl,
  resolveCoreApiPath,
  getDefaultUserSettingsUrl,
  getDefaultUserExtensionsUrl,
  getDefaultAdminConsoleUrl,
} from '../../utils/coreAssetUrl';
import { getAppStorageKeyPrefix, getApplicationCodeForApi } from '../../config/app.config';
import { subscribeProductTourSurface } from './product-tour.surface';
import { useOptionalTenant } from '../../context/TenantContext';
import './ProfileDropdown.css';

export interface ProfileCompany {
  id: string;
  name: string;
  logo: string | null;
}

export interface ProfileDropdownProps {
  /** Dışarıdan firma listesi (örn. CompanyContext); verilirse API çağrısı yapılmaz */
  profileCompanies?: ProfileCompany[];
  selectedCompanyId?: string;
  onCompanySwitch?: (companyId: string) => void | Promise<void>;
  settingsUrl?: string;
  adminConsoleUrl?: string;
  /** @deprecated Console visibility follows verified authority in this shared component; legacy caller flags are ignored. */
  showAdminConsole?: boolean;
  showManageAccount?: boolean;
  resolveAssetUrl?: (url: string | null | undefined) => string;
}

const SCOPE_GROUP_LABELS: Record<SupportedLanguage, string> = {
  tr: 'KAPSAMLAR',
  en: 'SCOPES',
  ar: 'النطاقات',
  de: 'BEREICHE',
  fr: 'PERIMETRES',
  es: 'AMBITOS',
  it: 'AMBITI',
  ru: 'ОБЛАСТИ',
  zh: '范围',
  ja: 'スコープ',
};

function readInitialCompanyId(): string {
  const prefix = getAppStorageKeyPrefix();
  const keys = [
    `${prefix}_selected_company_id`,
    `${prefix}_selected_company`,
    'qubepro_userSetting_defaultCompanyId',
  ];
  for (const k of keys) {
    const v = localStorage.getItem(k);
    if (v) return v;
  }
  return '';
}

function getCompanyInitials(name: string) {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join('');
}

/** Firma logosu: URL yok / DMS çözülmez / img hata → baş harfler (avatar ile aynı mantık). */
function ProfileCompanyLogo({
  logo,
  companyId,
  companyName,
}: {
  logo: string | null;
  companyId: string;
  companyName: string;
}) {
  // Stabil + immutable URL: presigned çözme/async yok → bootstrap'ta önyüklenmiş
  // cache'ten anında gelir, "flash" olmaz. Hata/boşsa baş harfler.
  const companyScope = resolveCompanyLogoMediaScopeId(companyId);
  const displayUrl = companyScope
    ? getDmsFileImageStableUrl(logo ?? null, { companyScope, systemMedia: true })
    : null;
  const [imgError, setImgError] = useState(false);

  useEffect(() => {
    setImgError(false);
  }, [logo, displayUrl]);

  const isSystemScope = companyId === SYSTEM_COMPANY_SCOPE_ID;
  const showPhoto = !isSystemScope && Boolean(displayUrl) && !imgError;
  const initials = getCompanyInitials(companyName) || '?';

  return (
    <div className={`profile-company-logo ${showPhoto ? 'has-image' : ''}`}>
      {isSystemScope ? (
        <Icon name="settings" size={18} />
      ) : (
        <>
          {showPhoto && displayUrl ? (
            <img
              src={displayUrl}
              alt={companyName}
              loading="eager"
              decoding="async"
              onError={() => setImgError(true)}
            />
          ) : null}
          {!showPhoto ? <span>{initials}</span> : null}
        </>
      )}
    </div>
  );
}

function defaultResolveAssetUrl(url: string | null | undefined): string {
  return resolveCoreAssetUrl(url);
}

export function ProfileDropdown({
  profileCompanies: externalCompanies,
  selectedCompanyId: externalSelectedId,
  onCompanySwitch: externalOnSwitch,
  settingsUrl = getDefaultUserSettingsUrl(),
  adminConsoleUrl = getDefaultAdminConsoleUrl(),
  showManageAccount = true,
  resolveAssetUrl: _resolveAssetUrl = defaultResolveAssetUrl,
}: ProfileDropdownProps = {}) {
  const { user, logout, language, updateLanguage, refreshUser } = useAuth();
  const companyContext = useOptionalCompany();
  const tenantContext = useOptionalTenant();
  const { theme, setTheme } = useTheme();
  const {
    preference: modalControlPlacement,
    setPreference: setModalControlPlacement,
  } = useModalControlPlacement();
  const t = useTranslation(language);
  const [avatarImageError, setAvatarImageError] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [isLanguageDropdownOpen, setIsLanguageDropdownOpen] = useState(false);
  const [isCompaniesExpandedMobile, setIsCompaniesExpandedMobile] = useState(false);
  const [internalCompanies, setInternalCompanies] = useState<ProfileCompany[]>([]);
  const [internalSelectedId, setInternalSelectedId] = useState<string>(readInitialCompanyId);
  const avatarRefreshAttemptedForUserRef = useRef<string | null>(null);
  const profileMenuOpenRef = useRef(isProfileMenuOpen);
  const languageDropdownOpenRef = useRef(isLanguageDropdownOpen);
  const productTourSurfaceBaselineRef = useRef<{
    profileMenuOpen: boolean;
    languageDropdownOpen: boolean;
  } | null>(null);
  profileMenuOpenRef.current = isProfileMenuOpen;
  languageDropdownOpenRef.current = isLanguageDropdownOpen;

  useEffect(() => subscribeProductTourSurface((surface) => {
    if (surface === null) {
      const baseline = productTourSurfaceBaselineRef.current;
      productTourSurfaceBaselineRef.current = null;
      if (!baseline) return;
      setIsProfileMenuOpen(baseline.profileMenuOpen);
      setIsLanguageDropdownOpen(baseline.languageDropdownOpen);
      return;
    }

    if (!productTourSurfaceBaselineRef.current) {
      productTourSurfaceBaselineRef.current = {
        profileMenuOpen: profileMenuOpenRef.current,
        languageDropdownOpen: languageDropdownOpenRef.current,
      };
    }
    setIsProfileMenuOpen(true);
    setIsLanguageDropdownOpen(surface === 'profile-language-menu');
  }), []);

  const hasExternalCompanyList = externalCompanies !== undefined;
  const usesCompanyContext = !hasExternalCompanyList && companyContext !== undefined;
  const hasBoundCompanyList = hasExternalCompanyList || usesCompanyContext;
  const contextCompanies = useMemo<ProfileCompany[]>(() => companyContext?.companies.map(company => ({
    id: company.id,
    name: company.name,
    logo: typeof company.logo === 'string' ? company.logo : null,
  })) ?? [], [companyContext?.companies]);
  const sourceProfileCompanies = hasExternalCompanyList ? (externalCompanies ?? [])
    : usesCompanyContext ? contextCompanies : internalCompanies;
  const canAccessSystemScope = usesCompanyContext ? companyContext.canAccessSystemScope : canUseSystemCompanyScope(user);
  const profileCompanies = canAccessSystemScope &&
    !sourceProfileCompanies.some((company) => company.id === SYSTEM_COMPANY_SCOPE_ID)
    ? [
        {
          id: SYSTEM_COMPANY_SCOPE_ID,
          name: getSystemCompanyScopeLabel(language),
          logo: null,
        },
        ...sourceProfileCompanies,
      ]
    : sourceProfileCompanies;
  const selectedCompanyId = hasExternalCompanyList ? (externalSelectedId ?? '')
    : usesCompanyContext ? companyContext.selectedCompanyId : internalSelectedId;
  const avatarCompanyScope = resolveSystemMediaCallerScopeId(selectedCompanyId);
  const avatarDisplayUrl = avatarCompanyScope
    ? getDmsFileImageStableUrl(user?.avatar ?? null, {
      companyScope: avatarCompanyScope,
      systemMedia: true,
    })
    : /^dms:FILE:[1-9]\d*$/.test(user?.avatar ?? '')
      ? `${resolveCoreApiPath('/api/auth/me/avatar/image')}?v=${encodeURIComponent(user?.avatar ?? '')}`
      : null;

  const storageCompanyKey = `${getAppStorageKeyPrefix()}_selected_company_id`;

  const fetchProfileCompanies = useCallback(async () => {
    if (hasBoundCompanyList || !user?.id) return;
    try {
      const res = await axios.get(resolveCoreApiPath('/api/users/me/companies'), {
        params: { appCode: getApplicationCodeForApi(), translations: language },
      });
      if (res.data?.success && Array.isArray(res.data.data)) {
        const data: ProfileCompany[] = res.data.data.map((c: { id: unknown; name?: string; logo?: string | null }) => ({
          id: String(c.id),
          name: c.name || String(c.id),
          logo: c.logo ?? null,
        }));
        setInternalCompanies(data);
        setInternalSelectedId((prev) => {
          if (prev && data.some((d) => d.id === prev)) return prev;
          if (data.length > 0) {
            const fallback = data[0].id;
            localStorage.setItem(storageCompanyKey, fallback);
            return fallback;
          }
          return prev;
        });
      }
    } catch {
      /* ignore */
    }
  }, [hasBoundCompanyList, user?.id, language, storageCompanyKey]);

  useEffect(() => {
    void fetchProfileCompanies();
  }, [fetchProfileCompanies]);

  // CompanyContext veya dış liste varsa logoların tek prefetch sahibi kaynağıdır.
  // Standalone kullanımda ProfileDropdown kendi listesini firma scope'larıyla prefetch eder.
  useEffect(() => {
    prefetchDmsImages([
      ...(avatarCompanyScope
        ? [{ ref: user?.avatar ?? null, companyScope: avatarCompanyScope, systemMedia: true }]
        : []),
      ...(hasBoundCompanyList
        ? []
        : profileCompanies.map((company) => ({
            ref: company.logo,
            companyScope: resolveCompanyLogoMediaScopeId(company.id),
            systemMedia: true,
          }))),
    ]);
  }, [avatarCompanyScope, hasBoundCompanyList, profileCompanies, user?.avatar]);

  useEffect(() => {
    setAvatarImageError(false);
  }, [user?.avatar, avatarDisplayUrl]);

  useEffect(() => {
    const userId = user?.id?.trim();
    if (!userId || user?.avatar?.trim()) return;
    if (avatarRefreshAttemptedForUserRef.current === userId) return;
    avatarRefreshAttemptedForUserRef.current = userId;
    const timer = window.setTimeout(() => {
      void refreshUser();
    }, 1500);
    return () => window.clearTimeout(timer);
  }, [refreshUser, user?.avatar, user?.id]);

  const handleCompanySwitch = (companyId: string) => {
    if (externalOnSwitch) {
      void externalOnSwitch(companyId);
      return;
    }
    if (usesCompanyContext) {
      void companyContext.switchCompany(companyId);
      return;
    }
    if (!hasExternalCompanyList) {
      setInternalSelectedId(companyId);
      localStorage.setItem(storageCompanyKey, companyId);
    }
  };

  const toggleProfileMenu = () => setIsProfileMenuOpen((o) => !o);
  const closeProfileMenu = () => setIsProfileMenuOpen(false);

  const handleThemeSelect = useCallback((nextTheme: Theme) => {
    if (nextTheme === theme) return;
    setTheme(nextTheme);
    axios.patch('/api/settings/user', { theme: nextTheme }).catch(() => { });
  }, [setTheme, theme]);

  const handleModalControlPlacementSelect = useCallback(
    (nextPreference: ModalControlPlacementPreference) => {
      if (nextPreference === modalControlPlacement) return;
      const previous = modalControlPlacement;
      setModalControlPlacement(nextPreference);
      axios
        .patch('/api/settings/user', {
          modalControlPlacement: nextPreference,
        })
        .catch(() => setModalControlPlacement(previous));
    },
    [modalControlPlacement, setModalControlPlacement],
  );

  const nextTheme: Theme = theme === 'light' ? 'dark' : 'light';
  const nextThemeLabel = nextTheme === 'dark'
    ? (t.common?.darkMode ?? 'Karanlık Mod')
    : (t.common?.lightMode ?? 'Aydınlık Mod');

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (isProfileMenuOpen && !target.closest('.profile-menu-container')) {
        setIsProfileMenuOpen(false);
        setIsLanguageDropdownOpen(false);
        setIsCompaniesExpandedMobile(false);
      }
      if (isLanguageDropdownOpen && !target.closest('.profile-language-select-wrap')) {
        setIsLanguageDropdownOpen(false);
      }
    };
    if (isProfileMenuOpen || isLanguageDropdownOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isProfileMenuOpen, isLanguageDropdownOpen]);

  const getUserInitials = () => {
    if (!user) return '?';
    const identity = user as typeof user & {
      fullName?: string | null;
      displayName?: string | null;
      name?: string | null;
    };
    const firstName = identity.firstName?.trim() || '';
    const lastName = identity.lastName?.trim() || '';
    const fullName = identity.fullName?.trim() || identity.displayName?.trim() || identity.name?.trim() || '';
    const emailLocalPart = identity.email?.split('@')[0]?.replace(/[._-]+/g, ' ').trim() || '';
    const parts = [firstName, lastName].filter(Boolean);
    const sourceParts = parts.length > 0 ? parts : (fullName || emailLocalPart).split(/\s+/).filter(Boolean);
    return sourceParts
      .slice(0, 2)
      .map((part) => part.charAt(0).toUpperCase())
      .join('') || '?';
  };

  const hasStoredAvatar = Boolean(user?.avatar?.trim());
  const showAvatarPhoto =
    Boolean(user) && hasStoredAvatar && Boolean(avatarDisplayUrl) && !avatarImageError;
  const showAvatarInitials = Boolean(user) && !showAvatarPhoto;

  const { canOpenAdminConsole } = useRoleAccess();

  const hasCompanies = profileCompanies.length > 0;
  const hasSystemScopeOption = profileCompanies.some((company) => company.id === '0');
  const showAdmin = canOpenAdminConsole;
  const extensionsUrl = user ? getDefaultUserExtensionsUrl() : null;

  return (
    <div className="profile-menu-container">
      <button
        className="profile-menu-button"
        data-tour-id="user-profile"
        onClick={toggleProfileMenu}
        aria-label={t.common.profileMenu || 'Profile menu'}
        aria-expanded={isProfileMenuOpen}
      >
        <div className={`profile-avatar ${showAvatarPhoto ? 'has-image' : ''}`}>
          {showAvatarPhoto && avatarDisplayUrl ? (
            <img
              src={avatarDisplayUrl}
              alt={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || ''}
              className="profile-avatar-image"
              onError={() => setAvatarImageError(true)}
            />
          ) : null}
          {showAvatarInitials ? <span className="profile-initials">{getUserInitials()}</span> : null}
        </div>
      </button>

      {isProfileMenuOpen && (
        <div className={`profile-dropdown${hasCompanies ? ' profile-dropdown-split' : ''}`}>
          {hasCompanies && (
            <div className={`profile-dropdown-companies${isCompaniesExpandedMobile ? ' profile-dropdown-companies-expanded' : ''}`}>
              <button
                type="button"
                className="profile-dropdown-companies-trigger"
                onClick={() => setIsCompaniesExpandedMobile((v) => !v)}
                aria-expanded={isCompaniesExpandedMobile}
              >
                <span className="profile-dropdown-companies-title">
                  {hasSystemScopeOption
                    ? SCOPE_GROUP_LABELS[language] ?? SCOPE_GROUP_LABELS.en
                    : t.sidebar?.companies ?? t.common?.companies ?? 'FİRMALAR'}
                </span>
                <Icon name="expandMore" size={18} className="profile-dropdown-companies-chevron" />
              </button>
              <div className="profile-dropdown-companies-content">
                <div className="profile-dropdown-companies-list">
                  {profileCompanies.map((company) => {
                    return (
                      <button
                        key={company.id}
                        className={`profile-company-item ${selectedCompanyId === company.id ? 'active' : ''}`}
                        onClick={() => handleCompanySwitch(company.id)}
                        title={company.name}
                      >
                        <ProfileCompanyLogo
                          logo={company.logo}
                          companyId={company.id}
                          companyName={company.name}
                        />
                        <span className="profile-company-name">{company.name}</span>
                        {selectedCompanyId === company.id && (
                          <Icon name="check" size={16} className="profile-company-check" />
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {hasCompanies && (
            <button
              onClick={() => {
                logout();
                closeProfileMenu();
              }}
              className="profile-dropdown-logout-bottom"
            >
              <Icon name="logout" size={18} />
              <span>{t.sidebar?.logout ?? 'Çıkış Yap'}</span>
            </button>
          )}

          <div className={hasCompanies ? 'profile-dropdown-profile' : ''}>
            <div className="profile-dropdown-header">
              <div className={`profile-dropdown-avatar ${showAvatarPhoto ? 'has-image' : ''}`}>
                {showAvatarPhoto && avatarDisplayUrl ? (
                  <img
                    src={avatarDisplayUrl}
                    alt={`${user?.firstName ?? ''} ${user?.lastName ?? ''}`.trim() || user?.email || ''}
                    className="profile-dropdown-avatar-image"
                    onError={() => setAvatarImageError(true)}
                  />
                ) : null}
                {showAvatarInitials ? (
                  <span className="profile-dropdown-initials">{getUserInitials()}</span>
                ) : null}
              </div>
              <div className="profile-dropdown-info">
                <div className="profile-dropdown-name">
                  {user?.firstName} {user?.lastName}
                </div>
                <div className="profile-dropdown-email">{user?.email}</div>
              </div>
            </div>

            <div className="profile-dropdown-divider" />

            <div className="profile-dropdown-menu">
              {tenantContext?.canSwitchWorkspace ? (
                <button
                  type="button"
                  className="profile-dropdown-item"
                  onClick={() => {
                    closeProfileMenu();
                    tenantContext.openWorkspaceChooser();
                  }}
                >
                  <Icon name="workspaceSwitch" size={18} />
                  <span>{language === 'tr' ? 'Çalışma Alanını Değiştir' : 'Switch Workspace'}</span>
                </button>
              ) : null}

              {showAdmin && (
                <a
                  href={adminConsoleUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-dropdown-item"
                  onClick={closeProfileMenu}
                >
                  <Icon name="settings" size={18} />
                  <span>{t.common?.adminConsole ?? 'Yönetici Konsolu'}</span>
                </a>
              )}

              {showManageAccount && (
                <a
                  href={settingsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-dropdown-item"
                  onClick={closeProfileMenu}
                >
                  <FiUser size={18} />
                  <span>{t.common?.manageAccount ?? t.common?.manageYourAccount ?? 'Hesabınızı Yönetin'}</span>
                </a>
              )}

              {extensionsUrl && t.common.userExtensions && (
                <a
                  href={extensionsUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="profile-dropdown-item"
                  onClick={closeProfileMenu}
                >
                  <Icon name="plug" size={18} />
                  <span>{t.common.userExtensions}</span>
                </a>
              )}

              <AppearancePreferencesView
                theme={theme === 'dark' ? 'dark' : 'light'}
                preference={modalControlPlacement}
                onToggleTheme={() => handleThemeSelect(nextTheme)}
                onPreferenceChange={handleModalControlPlacementSelect}
                labels={{
                  appearance: t.common.appearancePreference ?? 'Görünüm Tercihi',
                  toggleTheme: nextThemeLabel,
                  placement: t.common.modalControlPlacement ?? 'Form Kontrol Yerleşimi',
                  system: t.common.modalControlPlacementSystem ?? 'Sistem (Önerilen)',
                  windows: t.common.modalControlPlacementWindows ?? 'Windows',
                  macos: t.common.modalControlPlacementMacos ?? 'macOS',
                }}
              />

              <ProfileLanguageSelect
                label={t.common.selectLanguage}
                value={language}
                options={SUPPORTED_LANGUAGES.map(code => ({ value: code, label: NATIVE_LANGUAGE_NAMES[code] }))}
                open={isLanguageDropdownOpen}
                onOpenChange={setIsLanguageDropdownOpen}
                onChange={code => {
                  updateLanguage(code);
                  closeProfileMenu();
                }}
              />

              {!hasCompanies && (
                <button
                  onClick={() => {
                    logout();
                    closeProfileMenu();
                  }}
                  className="profile-dropdown-item profile-dropdown-logout"
                >
                  <Icon name="logout" size={18} />
                  <span>{t.sidebar?.logout ?? 'Çıkış Yap'}</span>
                </button>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
